import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertBanner } from '@flowcore-water/ui';
import type { User, ReactivationRequest, UserRole } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { useData } from '../contexts/DataContext';
import { useTheme } from '@flowcore-water/ui';

interface UserManagementProps {
  users: User[];
}

export const UserManagement: React.FC<UserManagementProps> = ({ users }) => {
  const { user: currentUser } = useAuth();
  const { updateUserRole, createUser, deactivateUser, reactivateUser, deprecateUser, getReactivationRequests, resolveReactivationRequest, refreshUsers, setExcludeFromProgress } = useData();
  const { t } = useTheme();
  const isReadOnly = currentUser?.role === 'outside_observer';
  const [searchQuery, setSearchQuery] = useState('');
  const [saving, setSaving] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [newRole, setNewRole] = useState('individual');
  const [creating, setCreating] = useState(false);
  const [showDeactivated, setShowDeactivated] = useState(false);

  // Reactivation requests
  const [requests, setRequests] = useState<ReactivationRequest[]>([]);
  const [loadingRequests, setLoadingRequests] = useState(true);

  // Modals
  const [deactivateModal, setDeactivateModal] = useState<User | null>(null);
  const [reactivateModal, setReactivateModal] = useState<{ request: ReactivationRequest; user: User | null } | null>(null);
  const [resetHistory, setResetHistory] = useState(false);
  const [newAccountRole, setNewAccountRole] = useState<UserRole>('individual');

  const loadRequests = useCallback(async () => {
    try {
      const reqs = await getReactivationRequests();
      setRequests(reqs);
    } catch { /* ignore */ }
    setLoadingRequests(false);
  }, [getReactivationRequests]);

  useEffect(() => { loadRequests(); }, [loadRequests]);

  const filteredUsers = useMemo(() => {
    return users.filter((u) => {
      if (!showDeactivated && u.deactivated) return false;
      if (u.deprecated) return false; // Always hide deprecated users from main list
      return (
        u.displayName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        u.email.toLowerCase().includes(searchQuery.toLowerCase())
      );
    });
  }, [users, searchQuery, showDeactivated]);

  const handleRoleChange = async (userId: string, newRole: string) => {
    if (userId === currentUser?.uid) {
      setError('You cannot change your own role');
      return;
    }
    setSaving(userId);
    setError(null);
    try {
      await updateUserRole(userId, newRole);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update role');
    } finally {
      setSaving(null);
    }
  };

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newEmail.trim()) return;
    setCreating(true);
    setError(null);
    try {
      await createUser(newEmail.trim(), newRole);
      setNewEmail('');
      setNewRole('individual');
      setShowCreateForm(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create user');
    } finally {
      setCreating(false);
    }
  };

  const handleDeactivate = async () => {
    if (!deactivateModal) return;
    setSaving(deactivateModal.uid);
    try {
      await deactivateUser(deactivateModal.uid);
      setDeactivateModal(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to deactivate');
    } finally {
      setSaving(null);
    }
  };

  const handleReactivate = async () => {
    if (!reactivateModal) return;
    setSaving(reactivateModal.request.userId);
    try {
      await reactivateUser(reactivateModal.request.userId, resetHistory);
      await resolveReactivationRequest(reactivateModal.request.id);
      setReactivateModal(null);
      setResetHistory(false);
      await loadRequests();
      await refreshUsers();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reactivate');
    } finally {
      setSaving(null);
    }
  };

  const handleNewAccount = async () => {
    if (!reactivateModal) return;
    setSaving(reactivateModal.request.userId);
    try {
      await deprecateUser(reactivateModal.request.userId);
      await createUser(reactivateModal.request.userEmail, newAccountRole);
      await resolveReactivationRequest(reactivateModal.request.id);
      setReactivateModal(null);
      setNewAccountRole('individual');
      await loadRequests();
      await refreshUsers();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create new account');
    } finally {
      setSaving(null);
    }
  };

  const deactivatedCount = users.filter((u) => u.deactivated && !u.deprecated).length;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold" style={{ color: t.textPrimary }}>User Management</h1>
          <p className="mt-2" style={{ color: t.textSecondary }}>Manage user roles and permissions</p>
        </div>
        {!isReadOnly && (
          <button
            onClick={() => setShowCreateForm(!showCreateForm)}
            className="rounded-lg border px-4 py-2 text-sm font-bold transition-colors"
            style={{ borderColor: t.accent + '80', background: t.accent + '1a', color: t.accent }}
          >
            {showCreateForm ? 'Cancel' : '+ New User'}
          </button>
        )}
      </div>

      {/* Reactivation Requests Banner */}
      {!loadingRequests && requests.length > 0 && (
        <div
          className="rounded-lg border p-4 space-y-3"
          style={{ background: t.warnBg, borderColor: t.warnBorder }}
        >
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-amber-500  text-xs font-bold" style={{ color: t.textPrimary }}>
              {requests.length}
            </span>
            <h2 className="text-sm font-bold text-amber-400 uppercase tracking-wide">
              Reactivation Request{requests.length > 1 ? 's' : ''}
            </h2>
          </div>
          {requests.map((req) => {
            const originalUser = users.find((u) => u.uid === req.userId);
            return (
              <div
                key={req.id}
                className="flex items-center justify-between rounded-lg border p-3"
                style={{ background: t.pageBg, borderColor: t.border }}
              >
                <div>
                  <p className="text-sm font-semibold" style={{ color: t.textPrimary }}>
                    {req.userName || req.userEmail}
                  </p>
                  <p className="text-xs" style={{ color: t.textSecondary }}>
                    <span className="font-mono">{req.userEmail}</span> attempted login on{' '}
                    {new Date(req.requestedAt).toLocaleDateString()}
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => { setReactivateModal({ request: req, user: originalUser || null }); setResetHistory(false); }}
                    className="rounded-lg border px-3 py-1.5 text-xs font-bold transition-colors"
                    style={{ borderColor: t.successBorder, background: t.successBg, color: t.success }}
                  >
                    Reactivate
                  </button>
                  <button
                    onClick={() => { setReactivateModal({ request: req, user: originalUser || null }); }}
                    className="rounded-lg border px-3 py-1.5 text-xs font-bold transition-colors"
                    style={{ borderColor: t.infoBorder, background: t.infoBg, color: t.info }}
                  >
                    New Employee
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create User Form */}
      {showCreateForm && (
        <form
          onSubmit={handleCreateUser}
          className="rounded-lg border p-6 space-y-4"
          style={{ background: t.cardBg, borderColor: t.accent + '4d' }}
        >
          <h2 className="text-sm font-bold uppercase tracking-wide" style={{ color: t.accent }}>Pre-register User</h2>
          <p className="text-xs" style={{ color: t.textSecondary }}>
            Create a placeholder — name and photo will be captured when they sign in with Google.
          </p>
          <div className="flex items-end gap-4">
            <div className="flex-1">
              <label className="block text-xs font-bold mb-1 uppercase tracking-wide" style={{ color: t.textSecondary }}>Email</label>
              <input
                type="email"
                required
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder="name@flowcorewater.com"
                className="w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-1"
                style={{ background: t.pageBg, borderColor: t.border, color: t.textPrimary }}
              />
            </div>
            <div>
              <label className="block text-xs font-bold mb-1 uppercase tracking-wide" style={{ color: t.textSecondary }}>Role</label>
              <select
                value={newRole}
                onChange={(e) => setNewRole(e.target.value)}
                className="rounded-lg border px-3 py-2 text-sm focus:outline-none"
                style={{ background: t.pageBg, borderColor: t.border, color: t.textPrimary }}
              >
                <option value="individual">Individual</option>
                <option value="instructor">Instructor</option>
                <option value="admin">Admin</option>
                <option value="outside_observer">Outside Observer</option>
              </select>
            </div>
            <button
              type="submit"
              disabled={creating || !newEmail.trim()}
              className="rounded-lg px-6 py-2 text-sm font-bold  transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ color: t.textPrimary, background: t.accent }}
            >
              {creating ? 'Creating...' : 'Create'}
            </button>
          </div>
        </form>
      )}

      {/* Search + Filters */}
      <div className="flex items-center gap-4">
        <input
          type="text"
          placeholder="Search by name or email..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full max-w-md rounded-lg border px-4 py-2 focus:outline-none focus:ring-1"
          style={{ background: t.pageBg, borderColor: t.border, color: t.textPrimary }}
        />
        {deactivatedCount > 0 && (
          <label className="flex items-center gap-2 text-xs whitespace-nowrap cursor-pointer" style={{ color: t.textSecondary }}>
            <input
              type="checkbox"
              checked={showDeactivated}
              onChange={(e) => setShowDeactivated(e.target.checked)}
              className="rounded"
            />
            Show deactivated ({deactivatedCount})
          </label>
        )}
      </div>

      {/* Error */}
      {error && <AlertBanner variant="error" message={error} onDismiss={() => setError(null)} />}

      {/* Users Table */}
      <div className="rounded-lg border overflow-hidden" style={{ background: t.cardBg, borderColor: t.border }}>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b" style={{ background: t.pageBg, borderColor: t.border }}>
                <th className="px-6 py-3 text-left font-bold text-xs uppercase tracking-wide" style={{ color: t.textSecondary }}>Name</th>
                <th className="px-6 py-3 text-left font-bold text-xs uppercase tracking-wide" style={{ color: t.textSecondary }}>Email</th>
                <th className="px-6 py-3 text-left font-bold text-xs uppercase tracking-wide" style={{ color: t.textSecondary }}>Role</th>
                <th className="px-6 py-3 text-left font-bold text-xs uppercase tracking-wide" style={{ color: t.textSecondary }}>Change Role</th>
                <th className="px-6 py-3 text-left font-bold text-xs uppercase tracking-wide" style={{ color: t.textSecondary }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((user) => {
                const isCurrentUser = user.uid === currentUser?.uid;
                const isSaving = saving === user.uid;
                const isPending = !user.displayName;

                return (
                  <tr
                    key={user.uid}
                    className="border-b transition-colors"
                    style={{
                      borderColor: t.border,
                      opacity: user.deactivated ? 0.5 : 1,
                    }}
                  >
                    <td className="px-6 py-4 font-medium" style={{ color: t.textPrimary }}>
                      {user.displayName || (
                        <span className="italic" style={{ color: t.textSecondary }}>Pending sign-in</span>
                      )}
                      {isCurrentUser && (
                        <span className="ml-2 text-xs font-bold rounded-full px-2 py-1 border whitespace-nowrap" style={{ background: t.accent + '33', color: t.accent, borderColor: t.accent + '4d' }}>
                          You
                        </span>
                      )}
                      {isPending && !user.deactivated && (
                        <span className="ml-2 text-xs font-bold rounded-full bg-amber-500/20 text-amber-400 px-2 py-1 border border-amber-500/30 whitespace-nowrap">
                          Pending
                        </span>
                      )}
                      {user.deactivated && (
                        <span className="ml-2 text-xs font-bold rounded-full bg-red-500/20 px-2 py-1 border border-red-500/30 whitespace-nowrap" style={{ color: t.fail }}>
                          Deactivated
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-xs font-mono" style={{ color: t.textSecondary }}>{user.email}</td>
                    <td className="px-6 py-4">
                      <span
                        className="inline-block rounded-full px-3 py-1 text-xs font-bold border whitespace-nowrap"
                        style={{
                          background: user.role === 'admin' ? t.failBg : user.role === 'outside_observer' ? t.sectionHeading + '33' : user.role === 'instructor' ? t.accent + '33' : t.border + '33',
                          color: user.role === 'admin' ? t.fail : user.role === 'outside_observer' ? t.sectionHeading : user.role === 'instructor' ? t.accent : t.textSecondary,
                          borderColor: user.role === 'admin' ? t.failBorder : user.role === 'outside_observer' ? t.sectionHeading + '4d' : user.role === 'instructor' ? t.accent + '4d' : t.border,
                        }}
                      >
                        {user.role === 'outside_observer' ? 'outside observer' : user.role}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      {isReadOnly || isCurrentUser || user.deactivated ? (
                        <span className="text-xs italic" style={{ color: t.textSecondary }}>
                          {isReadOnly ? '—' : isCurrentUser ? 'Cannot change own role' : 'Deactivated'}
                        </span>
                      ) : (
                        <select
                          value={user.role}
                          onChange={(e) => handleRoleChange(user.uid, e.target.value)}
                          disabled={isSaving}
                          className="rounded-lg border px-3 py-1 text-sm focus:outline-none disabled:opacity-50"
                          style={{ background: t.pageBg, borderColor: t.border, color: t.textPrimary }}
                        >
                          <option value="individual">Individual</option>
                          <option value="instructor">Instructor</option>
                          <option value="admin">Admin</option>
                          <option value="outside_observer">Outside Observer</option>
                        </select>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        {!user.deactivated && !isReadOnly && (
                          <>
                            <button
                              onClick={() => setExcludeFromProgress(user.uid, !user.excludeFromProgress)}
                              className="flex items-center gap-1.5 text-xs cursor-pointer select-none transition-colors"
                              style={{ color: user.excludeFromProgress ? t.accent : t.textMuted }}
                              title="Hide from progress dashboard"
                            >
                              <span
                                className="inline-flex items-center justify-center w-4 h-4 rounded transition-all"
                                style={{
                                  border: `1.5px solid ${user.excludeFromProgress ? t.accent : t.border}`,
                                  background: user.excludeFromProgress ? t.accent + '25' : 'transparent',
                                }}
                              >
                                {user.excludeFromProgress && (
                                  <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                                    <path d="M2 5.5L4 7.5L8 3" stroke={t.accent} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                  </svg>
                                )}
                              </span>
                              Observer
                            </button>
                            <Link
                              to={`/tree/${user.uid}`}
                              className="text-xs font-bold transition-colors"
                              style={{ color: t.accent }}
                            >
                              View Tree →
                            </Link>
                            {!isCurrentUser && (
                              <button
                                onClick={() => setDeactivateModal(user)}
                                className="text-xs font-bold  hover:text-red-300 transition-colors" style={{ color: t.fail }}
                              >
                                Deactivate
                              </button>
                            )}
                          </>
                        )}
                        {!isCurrentUser && user.deactivated && !user.deprecated && (
                          <button
                            onClick={() => {
                              reactivateUser(user.uid, false);
                              refreshUsers();
                            }}
                            className="text-xs font-bold transition-colors"
                            style={{ color: t.success }}
                          >
                            Reactivate
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {filteredUsers.length === 0 && (
          <div className="px-6 py-12 text-center">
            <p style={{ color: t.textSecondary }}>No users found</p>
          </div>
        )}
      </div>

      {/* Deactivation Confirmation Modal */}
      {deactivateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="rounded-xl border p-6 w-full max-w-md space-y-4" style={{ background: t.cardBg, borderColor: t.border }}>
            <h2 className="text-lg font-bold" style={{ color: t.textPrimary }}>
              Deactivate {deactivateModal.displayName || deactivateModal.email}?
            </h2>
            <p className="text-sm" style={{ color: t.textSecondary }}>
              This will prevent <span className="font-mono">{deactivateModal.email}</span> from logging in. Their training data will be preserved. You can reactivate them later.
            </p>
            <div className="flex justify-end gap-3 pt-2">
              <button
                onClick={() => setDeactivateModal(null)}
                className="rounded-lg border px-4 py-2 text-sm font-medium"
                style={{ borderColor: t.border, color: t.textSecondary }}
              >
                Cancel
              </button>
              <button
                onClick={handleDeactivate}
                disabled={!!saving}
                className="rounded-lg px-4 py-2 text-sm font-bold  bg-red-600 hover:bg-red-500 disabled:opacity-50" style={{ color: t.textPrimary }}
              >
                {saving ? 'Deactivating...' : 'Deactivate'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reactivation Modal */}
      {reactivateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="rounded-xl border p-6 w-full max-w-lg space-y-5" style={{ background: t.cardBg, borderColor: t.border }}>
            <h2 className="text-lg font-bold" style={{ color: t.textPrimary }}>
              Handle Request: {reactivateModal.request.userEmail}
            </h2>
            <p className="text-sm" style={{ color: t.textSecondary }}>
              {reactivateModal.request.userName && `${reactivateModal.request.userName} (${reactivateModal.request.userEmail})`}
              {' '}tried to log in on {new Date(reactivateModal.request.requestedAt).toLocaleDateString()}.
              Is this the same person or a new employee?
            </p>

            {/* Option 1: Reactivate */}
            <div className="rounded-lg border p-4 space-y-3" style={{ borderColor: t.successBorder, background: t.successBg }}>
              <h3 className="text-sm font-bold" style={{ color: t.success }}>Same Person — Reactivate Account</h3>
              <label className="flex items-center gap-2 text-sm cursor-pointer" style={{ color: t.textPrimary }}>
                <input
                  type="checkbox"
                  checked={resetHistory}
                  onChange={(e) => setResetHistory(e.target.checked)}
                  className="rounded"
                />
                Reset training history (archive old progress)
              </label>
              <button
                onClick={handleReactivate}
                disabled={!!saving}
                className="rounded-lg px-4 py-2 text-sm font-bold  disabled:opacity-50"
                style={{ color: t.buttonText, background: t.success }}
              >
                {saving ? 'Processing...' : resetHistory ? 'Reactivate & Reset' : 'Reactivate (Keep History)'}
              </button>
            </div>

            {/* Option 2: New Employee */}
            <div className="rounded-lg border p-4 space-y-3" style={{ borderColor: t.infoBorder, background: t.infoBg }}>
              <h3 className="text-sm font-bold" style={{ color: t.info }}>New Employee — Create Fresh Account</h3>
              <p className="text-xs" style={{ color: t.textSecondary }}>
                The old account will be archived. All previous training data stays in the database but is marked as belonging to the former employee.
              </p>
              <div className="flex items-center gap-3">
                <label className="text-xs font-bold" style={{ color: t.textSecondary }}>Role:</label>
                <select
                  value={newAccountRole}
                  onChange={(e) => setNewAccountRole(e.target.value as UserRole)}
                  className="rounded-lg border px-3 py-1 text-sm"
                  style={{ background: t.pageBg, borderColor: t.border, color: t.textPrimary }}
                >
                  <option value="individual">Individual</option>
                  <option value="instructor">Instructor</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <button
                onClick={handleNewAccount}
                disabled={!!saving}
                className="rounded-lg px-4 py-2 text-sm font-bold  disabled:opacity-50"
                style={{ color: t.buttonText, background: t.info }}
              >
                {saving ? 'Processing...' : 'Create New Account'}
              </button>
            </div>

            <div className="flex justify-end pt-2">
              <button
                onClick={() => { setReactivateModal(null); setResetHistory(false); }}
                className="rounded-lg border px-4 py-2 text-sm font-medium"
                style={{ borderColor: t.border, color: t.textSecondary }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
